import {React, useState, useRef, useEffect, useMemo} from 'react'
import {Modal, Button, ButtonGroup, InputGroup, UncontrolledTooltip, Spinner} from 'reactstrap'
import {removeBackground} from '@imgly/background-removal'
import classNames from 'classnames'
import {ReactComponent as Revert} from '../../../../assets/icons/revert.svg'
import {ReactComponent as AutoRemove} from '../../../../assets/icons/auto-remove.svg'
import {ReactComponent as Close} from '../../../../assets/icons/close.svg'
import {ReactComponent as Minus} from '../../../../assets/icons/minus.svg'
import {ReactComponent as Plus} from '../../../../assets/icons/plus.svg'
import {ReactComponent as Palm} from '../../../../assets/icons/palm.svg'
import {ReactComponent as Undo} from '../../../../assets/icons/undo.svg'
import {ReactComponent as Redo} from '../../../../assets/icons/redo.svg'
import {ReactComponent as Check} from '../../../../assets/icons/check.svg'
import useUploadFile from '../../../../hooks/useUploadFile'
import {REMOVE_ACTION, buildCursorDataUrl} from './remove-background'
import {actionToggleRemoveBackgroundMode} from '../../context/SelectedVariantContext/action'

// Model configs
const WEBGPU_MODEL_ID = 'Xenova/modnet'
const FALLBACK_MODEL_ID = 'briaai/RMBG-1.4'

// iOS detection
const isIOS = () => {
    return (
        ['iPad Simulator', 'iPhone Simulator', 'iPod Simulator', 'iPad', 'iPhone', 'iPod'].includes(
            navigator.platform
        ) ||
        (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    )
}

// State
const state = {
    model: null,
    processor: null,
    isWebGPUSupported: false,
    currentModelId: FALLBACK_MODEL_ID,
    isIOS: isIOS(),
}

const RemoveBackgroundModal = (props) => {
    const {isOpen, toggle, imageSrc, selectedLayer, dispatch} = props

    const canvasRef = useRef(null)
    const imageRef = useRef(null)
    const canvasContainerRef = useRef(null)
    const lastPoint = useRef(null)
    const [ctx, setCtx] = useState(null)
    // State để tách biệt click và kéo chuột
    const [isDrag, setIsDrag] = useState(false)
    const [mouseDownPos, setMouseDownPos] = useState(null)
    const DRAG_THRESHOLD = 5 // px
    const hasMouseMoved = useRef(false) // legacy, sẽ bỏ sau
    const [isDrawing, setIsDrawing] = useState(false)
    const [brushSize, setBrushSize] = useState(35)
    const brushSizeRef = useRef(brushSize)
    const [mode, setMode] = useState(REMOVE_ACTION.ERASE)
    const [undoStack, setUndoStack] = useState([])
    const [redoStack, setRedoStack] = useState([])
    const [offset, setOffset] = useState({x: 0, y: 0})

    const [isPanning, setIsPanning] = useState(false)
    const [panStart, setPanStart] = useState(null)

    const [scalePercent, setScalePercent] = useState(100)
    const [canRestore, setCanRestore] = useState(false)
    const [isSpacePan, setIsSpacePan] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isApplying, setIsApplying] = useState(false)

    const [autoRemoveBgState, setAutoRemoveBgState] = useState(state)
    const autoRemoveFn = window.autoRemoveBackground.createBgRemoveModule(autoRemoveBgState)
    const {processImage, initializeModel} = autoRemoveFn

    const [isLoadingQuickRemove, setIsLoadingQuickRemove] = useState(false)
    const [url, setUrl] = useState(null)

    const MIN_ZOOM = 10
    const MAX_ZOOM = 200

    const getScale = () => scalePercent / 100
    const {uploadFileWithUpdateLayer} = useUploadFile()

    const increaseZoom = () => {
        setScalePercent((prevPercent) => Math.min(prevPercent + 10, MAX_ZOOM))
    }

    const decreaseZoom = () => {
        setScalePercent((prevPercent) => Math.max(prevPercent - 10, MIN_ZOOM))
    }

    useEffect(() => {
        const slider = document.getElementById('brush-size-input')
        slider?.addEventListener('input', function () {
            const value = ((parseInt(slider.value) - 10) / 90) * 100
            this.style.background = `linear-gradient(to right, #007bff ${value}%, #e0e0e0 ${value}%)`
        })
    }, [isOpen, brushSize, brushSizeRef.current])

    //Zoom-in, out using mouse wheel
    const handleWheel = (e) => {
        if (e.ctrlKey) {
            e.preventDefault()
            if (e.deltaY < 0) {
                increaseZoom()
            } else {
                decreaseZoom()
            }
        }
    }

    const resetAll = () => {
        setMode(REMOVE_ACTION.ERASE)
        // setBrushSize(35)
        setScalePercent(100)
        setOffset({x: 0, y: 0})
        setCanRestore(false)
        if (undoStack.length > 0) {
            ctx.putImageData(undoStack[0], 0, 0)
            setUndoStack([undoStack[0]])
        }
    }

    const handleImageLoad = () => {
        initializeCanvas()
        setIsLoading(false)
    }

    const initializeCanvas = () => {
        const canvas = canvasRef.current
        const image = imageRef.current
        const canvasContainer = canvasContainerRef.current
        if (!canvas || !image || !canvasContainer || !image.complete) return

        const context = canvas.getContext('2d', {willReadFrequently: true})

        setScalePercent(100)
        setOffset({x: 0, y: 0})

        const containerWidth = canvasContainer.offsetWidth
        const containerHeight = canvasContainer.offsetHeight
        const imageWidth = image.naturalWidth
        const imageHeight = image.naturalHeight
        let newWidth, newHeight
        if (imageWidth / imageHeight > containerWidth / containerHeight) {
            newWidth = containerWidth
            newHeight = newWidth * (imageHeight / imageWidth)
        } else {
            newHeight = containerHeight
            newWidth = newHeight * (imageWidth / imageHeight)
        }

        canvas.width = newWidth
        canvas.height = newHeight

        context.drawImage(image, 0, 0, newWidth, newHeight)
        const imageData = context.getImageData(0, 0, canvas.width, canvas.height)
        setUndoStack([imageData])
        setCtx(context)
        canvas.style.cursor = buildCursorDataUrl(mode, brushSize)
    }

    const startDrawing = (e) => {
        setIsDrawing(true)
        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()
        const currentScale = getScale()
        lastPoint.current = {
            x: (e.clientX - rect.left) / currentScale,
            y: (e.clientY - rect.top) / currentScale,
        }

        draw(e)
    }

    const endDrawing = () => {
        if (!isDrawing || mode === REMOVE_ACTION.NONE || (mode === REMOVE_ACTION.RESTORE && undoStack.length <= 1))
            return
        setIsDrawing(false)
        lastPoint.current = null
        // Nếu là drag thì mới push undo, single click thì đã push ở mouseup
        if (isDrag) {
            const imageData = ctx.getImageData(0, 0, canvasRef.current.width, canvasRef.current.height)
            setUndoStack((prev) => [...prev, imageData])
            setRedoStack([])
        }
    }

    // Xoá 1 điểm nhỏ tại vị trí (canvasX, canvasY)
    const erasePoint = (canvasX, canvasY) => {
        if (!ctx || mode !== REMOVE_ACTION.ERASE) return
        const radius = brushSize / 2
        ctx.save()
        ctx.globalCompositeOperation = 'destination-out'
        ctx.beginPath()
        ctx.arc(canvasX, canvasY, radius, 0, 2 * Math.PI)
        ctx.fill()
        ctx.restore()
        if (!canRestore) setCanRestore(true)
    }

    // Khôi phục 1 điểm nhỏ tại vị trí (canvasX, canvasY)
    const restorePoint = (canvasX, canvasY) => {
        if (!ctx || mode !== REMOVE_ACTION.RESTORE) return
        const canvas = canvasRef.current
        const image = imageRef.current
        if (!canvas || !image) return
        const radius = brushSize / 2
        // Tạo offscreen canvas để lấy lại pixel gốc
        const offscreenCanvas = document.createElement('canvas')
        offscreenCanvas.width = canvas.width
        offscreenCanvas.height = canvas.height
        const offscreenCtx = offscreenCanvas.getContext('2d')
        offscreenCtx.drawImage(image, 0, 0, canvas.width, canvas.height)
        offscreenCtx.globalCompositeOperation = 'destination-in'
        offscreenCtx.beginPath()
        offscreenCtx.arc(canvasX, canvasY, radius, 0, 2 * Math.PI)
        offscreenCtx.fill()
        ctx.save()
        ctx.globalCompositeOperation = 'source-over'
        ctx.drawImage(offscreenCanvas, 0, 0)
        ctx.restore()
    }

    const draw = (e) => {
        if (!isDrawing || !ctx || isDisable || mode === REMOVE_ACTION.NONE) return

        const canvas = canvasRef.current
        const rect = canvas.getBoundingClientRect()

        const currentScale = getScale()
        const canvasX = (e.clientX - rect.left) / currentScale
        const canvasY = (e.clientY - rect.top) / currentScale
        const radius = brushSize / (2 * currentScale)
        ctx.save()
        ctx.lineWidth = radius * 2
        ctx.lineJoin = 'round'
        ctx.lineCap = 'round'
        ctx.beginPath()
        if (lastPoint.current) {
            ctx.moveTo(lastPoint.current.x, lastPoint.current.y)
        }
        ctx.lineTo(canvasX, canvasY)

        if (mode === REMOVE_ACTION.ERASE) {
            ctx.globalCompositeOperation = 'destination-out'
            ctx.stroke()
            if (!canRestore) setCanRestore(true)
        } else if (mode === REMOVE_ACTION.RESTORE && undoStack.length > 0) {
            const offscreenCanvas = document.createElement('canvas')
            offscreenCanvas.width = canvas.width
            offscreenCanvas.height = canvas.height
            const offscreenCtx = offscreenCanvas.getContext('2d')

            offscreenCtx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height)

            offscreenCtx.globalCompositeOperation = 'destination-in'

            offscreenCtx.lineWidth = radius * 2
            offscreenCtx.lineJoin = 'round'
            offscreenCtx.lineCap = 'round'
            offscreenCtx.strokeStyle = 'black' // color is not important here

            offscreenCtx.beginPath()
            if (lastPoint.current) {
                offscreenCtx.moveTo(lastPoint.current.x, lastPoint.current.y)
            }
            offscreenCtx.lineTo(canvasX, canvasY)
            offscreenCtx.stroke()

            ctx.drawImage(offscreenCanvas, 0, 0)
        }

        ctx.restore()

        // update last point
        lastPoint.current = {x: canvasX, y: canvasY}
    }

    const handleUndo = () => {
        if (undoStack.length > 1) {
            const newUndoStack = undoStack.slice(0, -1)
            const lastState = undoStack[undoStack.length - 1]
            setUndoStack(newUndoStack)
            setRedoStack((prev) => [...prev, lastState])
            ctx.putImageData(newUndoStack[newUndoStack.length - 1], 0, 0)
        }
    }

    const handleRedo = () => {
        if (redoStack.length > 0) {
            const lastRedo = redoStack[redoStack.length - 1]
            setRedoStack((prev) => prev.slice(0, -1))
            setUndoStack((prev) => [...prev, lastRedo])
            ctx.putImageData(lastRedo, 0, 0)
        }
    }

    const handleApply = async () => {
        setIsApplying(true)
        const visibleCanvas = canvasRef.current
        const originalImage = imageRef.current
        if (!visibleCanvas || !originalImage) {
            setIsApplying(false)
            return
        }

        try {
            setTimeout(() => {
                //Create a new canvas with the original image size
                const offscreenCanvas = document.createElement('canvas')
                offscreenCanvas.width = originalImage.naturalWidth
                offscreenCanvas.height = originalImage.naturalHeight
                const offscreenCtx = offscreenCanvas.getContext('2d')

                offscreenCtx.drawImage(originalImage, 0, 0)

                const maskCanvas = document.createElement('canvas')
                maskCanvas.width = visibleCanvas.width
                maskCanvas.height = visibleCanvas.height
                const maskCtx = maskCanvas.getContext('2d')
                maskCtx.putImageData(ctx.getImageData(0, 0, visibleCanvas.width, visibleCanvas.height), 0, 0)

                //destination-in: keep only the pixels that are in both the source and destination
                offscreenCtx.globalCompositeOperation = 'destination-in'
                offscreenCtx.drawImage(maskCanvas, 0, 0, offscreenCanvas.width, offscreenCanvas.height)

                offscreenCanvas.toBlob(async (blob) => {
                    if (!blob) {
                        setIsApplying(false)
                        return
                    }
                    const originalFilename = selectedLayer?.text || 'edited-image.jpg'
                    const filenameWithoutExtension =
                        originalFilename.substring(0, originalFilename.lastIndexOf('.')) || originalFilename
                    const filename = `${filenameWithoutExtension}.png`
                    const file = new File([blob], filename, {type: 'image/png'})
                    const objectUrl = URL.createObjectURL(blob)
                    const isSuccess = await uploadFileWithUpdateLayer(objectUrl, file, selectedLayer)
                    if (isSuccess) {
                        toggle()
                    }
                    setIsApplying(false)
                    dispatch(actionToggleRemoveBackgroundMode(false))
                }, 'image/png')
            }, 0)
        } catch (error) {
            setIsApplying(false)
        }
    }

    // Save current canvas state to allow undo
    const pushUndo = () => {
        const canvas = canvasRef.current
        if (!ctx || !canvas) return
        const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height)
        setUndoStack((prev) => {
            const MAX = 20
            const next = [...prev, snapshot]
            return next.length > MAX ? next.slice(-MAX) : next
        })
    }
    // Automatically remove background then apply the difference as an erase mask
    const handleQuickRemove = async () => {
        if (!imageRef.current || !ctx) return
        setIsLoadingQuickRemove(true)
        try {
            // 1. Call API to get processed image with background removed
            const resultBlob = await processImage(imageSrc, autoRemoveBgState)

            // 2. Convert blob to HTMLImageElement
            const processedImg = await new Promise((resolve, reject) => {
                const img = new Image()
                img.onload = () => resolve(img)
                img.onerror = reject
                img.src = URL.createObjectURL(resultBlob)
            })

            const canvas = canvasRef.current
            const wDisp = canvas.width // displayed size
            const hDisp = canvas.height

            // Use natural (original) resolution for accurate comparison
            const imgNatW = imageRef.current.naturalWidth
            const imgNatH = imageRef.current.naturalHeight

            // 3. Draw original and processed images to off-screen canvases at NATURAL size
            const oriCvs = document.createElement('canvas')
            oriCvs.width = imgNatW
            oriCvs.height = imgNatH
            const oriCtx = oriCvs.getContext('2d')
            oriCtx.drawImage(imageRef.current, 0, 0, imgNatW, imgNatH)

            const proCvs = document.createElement('canvas')
            proCvs.width = imgNatW
            proCvs.height = imgNatH
            const proCtx = proCvs.getContext('2d')
            proCtx.drawImage(processedImg, 0, 0, imgNatW, imgNatH)

            const oriData = oriCtx.getImageData(0, 0, imgNatW, imgNatH)
            const proData = proCtx.getImageData(0, 0, imgNatW, imgNatH)

            // 4. Build mask where pixels were removed (alpha threshold)
            const maskCanvas = document.createElement('canvas')
            maskCanvas.width = imgNatW
            maskCanvas.height = imgNatH
            const maskCtx = maskCanvas.getContext('2d')
            const maskData = maskCtx.createImageData(imgNatW, imgNatH)

            for (let i = 0; i < oriData.data.length; i += 4) {
                const oriA = oriData.data[i + 3]
                const proA = proData.data[i + 3]
                if (oriA > 0 && proA < 20) {
                    // White pixel on mask means erase here
                    maskData.data[i] = 255
                    maskData.data[i + 1] = 255
                    maskData.data[i + 2] = 255
                    maskData.data[i + 3] = 255
                }
            }
            maskCtx.putImageData(maskData, 0, 0)

            // 5. Apply mask to visible canvas as destination-out (erase)
            ctx.save()
            ctx.globalCompositeOperation = 'destination-out'
            ctx.imageSmoothingEnabled = true
            ctx.imageSmoothingQuality = 'high'
            ctx.drawImage(maskCanvas, 0, 0, imgNatW, imgNatH, 0, 0, wDisp, hDisp)
            ctx.restore()

            // 6. Update undo stack and enable restore
            pushUndo()
            if (!canRestore) setCanRestore(true)
        } catch (error) {

        } finally {
            setIsLoadingQuickRemove(false)
        }
    }

    useEffect(() => {
        if (isOpen && selectedLayer) {
            setIsLoading(true)
            const image = imageRef.current
            if (image && image.complete) {
                handleImageLoad()
            }
        }

        return () => {
            setUrl(null)
        }
    }, [isOpen, selectedLayer, imageSrc])

    //Undo: Ctrl+Z, Move: Space
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (e.code === 'Space') setIsSpacePan(true)
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyZ') {
                handleUndo()
            }
        }
        const handleKeyUp = (e) => {
            if (e.code === 'Space') setIsSpacePan(false)
        }
        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)
        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [undoStack, ctx])

    //Handle mouse down for dragging outside the canvas
    useEffect(() => {
        const handleMouseDown = (e) => {
            if (e.button !== 0 || mode === REMOVE_ACTION.NONE) return
            window.isMousePressed = true
            setIsDrag(false)
            setMouseDownPos({x: e.clientX, y: e.clientY})
            hasMouseMoved.current = false // legacy

            const canvas = canvasRef.current
            if (!canvas) return

            const rect = canvas.getBoundingClientRect()
            const currentScale = getScale()
            const canvasX = (e.clientX - rect.left) / currentScale
            const canvasY = (e.clientY - rect.top) / currentScale
            const isInsideCanvas = canvasX >= 0 && canvasX <= canvas.width && canvasY >= 0 && canvasY <= canvas.height

            if (mode === REMOVE_ACTION.DRAG || isSpacePan) {
                setIsPanning(true)
                setPanStart({x: e.clientX - offset.x, y: e.clientY - offset.y})
                e.preventDefault()
            } else if (isInsideCanvas && !isPanning) {
                startDrawing(e)
            }
        }

        const handleMouseUp = (e) => {
            if (e.button !== 0) return
            window.isMousePressed = false

            // Single click erase/restore nếu không phải drag
            if (
                (mode === REMOVE_ACTION.ERASE || (mode === REMOVE_ACTION.RESTORE && undoStack.length > 1)) &&
                !isDrag &&
                !isPanning
            ) {
                const canvas = canvasRef.current
                if (canvas) {
                    const rect = canvas.getBoundingClientRect()
                    const currentScale = getScale()
                    const canvasX = (e.clientX - rect.left) / currentScale
                    const canvasY = (e.clientY - rect.top) / currentScale
                    if (canvasX >= 0 && canvasX <= canvas.width && canvasY >= 0 && canvasY <= canvas.height) {
                        if (mode === REMOVE_ACTION.ERASE) {
                            erasePoint(canvasX, canvasY)
                        } else if (mode === REMOVE_ACTION.RESTORE) {
                            restorePoint(canvasX, canvasY)
                        }
                        // Lưu undo
                        const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height)
                        setUndoStack((prev) => [...prev, imageData])
                        setRedoStack([])
                    }
                }
            }

            setIsDrag(false)
            setMouseDownPos(null)

            if (isDrawing) {
                endDrawing()
            }
            if (isPanning) {
                setIsPanning(false)
            }
        }

        const handleMouseMove = (e) => {
            const canvas = canvasRef.current
            if (!canvas || mode === REMOVE_ACTION.NONE) return

            const rect = canvas.getBoundingClientRect()
            const currentScale = getScale()
            const canvasX = (e.clientX - rect.left) / currentScale
            const canvasY = (e.clientY - rect.top) / currentScale
            const isInsideCanvas = canvasX >= 0 && canvasX <= canvas.width && canvasY >= 0 && canvasY <= canvas.height

            // Phân biệt drag hay click
            if (window.isMousePressed && mouseDownPos) {
                const dx = e.clientX - mouseDownPos.x
                const dy = e.clientY - mouseDownPos.y
                if (Math.sqrt(dx * dx + dy * dy) > DRAG_THRESHOLD && !isDrag) {
                    setIsDrag(true)
                }
            }

            // If mouse is pressed and inside canvas, start drawing if not already drawing
            if (
                window.isMousePressed &&
                isInsideCanvas &&
                !isDrawing &&
                !isPanning &&
                mode !== REMOVE_ACTION.DRAG &&
                !isSpacePan
            ) {
                startDrawing(e)
            }

            // Continue drawing if already drawing
            if (isDrawing && ctx) {
                if (isInsideCanvas) {
                    draw(e)
                }
            }

            // Handle panning
            if (isPanning && panStart) {
                setOffset({x: e.clientX - panStart.x, y: e.clientY - panStart.y})
            }
        }

        window.addEventListener('mousedown', handleMouseDown)
        window.addEventListener('mouseup', handleMouseUp)
        window.addEventListener('mousemove', handleMouseMove)

        return () => {
            window.removeEventListener('mousedown', handleMouseDown)
            window.removeEventListener('mouseup', handleMouseUp)
            window.removeEventListener('mousemove', handleMouseMove)
        }
    }, [mode, isSpacePan, offset, isDrawing, ctx, isPanning, panStart])

    useEffect(() => {
        setIsDrawing(false)
    }, [mode])

    // Prevent some actions
    useEffect(() => {
        const preventCtrlADelete = (e) => {
            // Prevent Ctrl+A (select all)
            if ((e.ctrlKey || e.metaKey) && e.code === 'KeyA') {
                e.preventDefault()
            }
        }

        if (isOpen) {
            window.addEventListener('keydown', preventCtrlADelete)
        }

        return () => {
            window.removeEventListener('keydown', preventCtrlADelete)
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) {
            resetAll()
            setBrushSize(35)
        }
    }, [isOpen])

    useEffect(() => {
        if (!canvasRef.current) return
        if (mode === REMOVE_ACTION.DRAG || isSpacePan) {
            canvasRef.current.style.cursor = 'pointer'
        } else if (mode === REMOVE_ACTION.ERASE || mode === REMOVE_ACTION.RESTORE) {
            canvasRef.current.style.cursor = buildCursorDataUrl(mode, brushSize)
        } else {
            canvasRef.current.style.cursor = 'default'
        }
    }, [mode, brushSize, isSpacePan])

    const initializeModelFn = async () => {
        const newState = await initializeModel(autoRemoveBgState)
        setAutoRemoveBgState(newState)
    }

    useEffect(() => {
        initializeModelFn()
    }, [])

    const isDisable = useMemo(() => {
        return isLoadingQuickRemove || isApplying
    }, [isLoadingQuickRemove, isApplying])

    return (
        <Modal isOpen={isOpen} className="ModalRemoveBackground">
            <div className="d-flex justify-content-between">
                <div className="LeftSide">
                    <div className="Toolbar">
                        <div className="HeaderLeft">
                            <ButtonGroup className="my-2">
                                <Button
                                    id="btn-undo"
                                    color="default"
                                    className="UndoButton"
                                    onClick={handleUndo}
                                    disabled={isDisable}
                                >
                                    <Undo width={20} height={20} />
                                </Button>
                                <Button
                                    id="btn-redo"
                                    color="default"
                                    className="RedoButton"
                                    onClick={handleRedo}
                                    disabled={isDisable}
                                >
                                    <Redo width={20} height={20} />
                                </Button>
                            </ButtonGroup>
                        </div>
                        <div className="HeaderRight d-flex">
                            <div
                                id="drag-stage"
                                className={classNames('MockupDrag d-flex justify-content-center align-items-center', {
                                    Active: mode === REMOVE_ACTION.DRAG,
                                })}
                                onClick={() => {
                                    if (mode === REMOVE_ACTION.DRAG) {
                                        setMode(REMOVE_ACTION.NONE)
                                    } else {
                                        setMode(REMOVE_ACTION.DRAG)
                                    }
                                }}
                            >
                                <Palm width={20} height={20} />
                            </div>
                            <div className="MockupZoom">
                                <InputGroup>
                                    <Button
                                        id="btn-minus"
                                        className="BtnMinus"
                                        color="default"
                                        onClick={decreaseZoom}
                                        disabled={isDisable}
                                    >
                                        <Minus width={11} />
                                    </Button>
                                    <div className="TextRatio">{`${scalePercent}%`}</div>
                                    <Button
                                        id="btn-plus"
                                        className="BtnPlus"
                                        color="default"
                                        onClick={increaseZoom}
                                        disabled={isDisable}
                                    >
                                        <Plus width={11} />
                                    </Button>
                                </InputGroup>
                            </div>
                            <UncontrolledTooltip target="drag-stage">Drag editor</UncontrolledTooltip>
                            <UncontrolledTooltip target="btn-minus">Zoom out</UncontrolledTooltip>
                            <UncontrolledTooltip target="btn-plus">Zoom in</UncontrolledTooltip>
                            <UncontrolledTooltip target="btn-undo">Undo</UncontrolledTooltip>
                            <UncontrolledTooltip target="btn-redo">Redo</UncontrolledTooltip>
                        </div>
                    </div>
                    <div
                        className="Content"
                        ref={canvasContainerRef}
                        style={{height: `calc(100% - 68px)`}}
                        onWheel={handleWheel}
                    >
                        <div className="Canvas">
                            {isLoading && <Spinner style={{color: '#007BFF'}} />}
                            <canvas
                                disabled={isDisable || mode === REMOVE_ACTION.NONE}
                                ref={canvasRef}
                                style={{
                                    display: isLoading ? 'none' : 'block',
                                    transform: `translate(${offset.x}px, ${offset.y}px) scale(${getScale()})`,
                                    transformOrigin: 'center center',
                                }}
                                onMouseDown={(e) => {
                                    // Mouse down is now handled globally
                                }}
                                onMouseMove={(e) => {
                                    // Mouse move is now handled globally
                                }}
                                onMouseUp={() => {
                                    // Mouse up is now handled globally
                                }}
                                onMouseLeave={() => {
                                    // Mouse leave is now handled globally
                                }}
                                onContextMenu={(e) => e.preventDefault()}
                            />
                            <img
                                ref={imageRef}
                                src={url ? url : imageSrc}
                                alt="Editable"
                                onLoad={handleImageLoad}
                                crossOrigin="anonymous"
                                style={{display: 'none'}}
                            />
                        </div>
                    </div>
                </div>
                <div className="RightSide d-flex flex-column justify-content-between">
                    <div>
                        <div className="RemoveTitle">
                            Remove background
                            <Close onClick={isDisable ? null : toggle} className="CloseIcon" />
                        </div>

                        <Button
                            color="default"
                            className="QuickRemoveButton"
                            onClick={handleQuickRemove}
                            disabled={isDisable}
                        >
                            <AutoRemove />
                            <span className="ms-2">{isLoadingQuickRemove ? <Spinner size="sm" /> : 'Auto remove'}</span>
                        </Button>

                        <div className="AdvancedRemover">Advanced remover</div>
                        <div className="CustomTitle mt-2">Customize brush parameters</div>
                        <div className="CustomTitle mt-3 mb-2">Select mode</div>
                        <div className="d-flex align-items-center mb-3">
                            <div className="ButtonWrap d-flex align-items-center flex-column">
                                <button
                                    className={`ButtonCircle ${mode === REMOVE_ACTION.ERASE ? 'active' : ''}`}
                                    onClick={() => setMode(REMOVE_ACTION.ERASE)}
                                    disabled={isDisable}
                                >
                                    <Minus width={10.5} />
                                    <div className="CheckmarkBadge">
                                        <Check width={6.5} height={5} className="CheckmarkIcon" />
                                    </div>
                                </button>
                                <span className="CustomTitle">Erase</span>
                            </div>

                            <div className="ButtonWrap d-flex align-items-center flex-column ms-2">
                                <button
                                    className={`ButtonCircle ${mode === REMOVE_ACTION.RESTORE ? 'active' : ''}`}
                                    onClick={() => setMode(REMOVE_ACTION.RESTORE)}
                                    disabled={!canRestore || isDisable || undoStack.length <= 1}
                                >
                                    <Plus width={10.5} />
                                    <div className="CheckmarkBadge">
                                        <Check width={6.5} height={5} className="CheckmarkIcon" />
                                    </div>
                                </button>
                                <span className="CustomTitle">Restore</span>
                            </div>
                        </div>

                        <div className="d-flex align-items-center justify-content-between mb-1">
                            <span className="CustomTitle">Brush size</span>
                            <div className="BrushSize"> {brushSize} px</div>
                        </div>
                        <input
                            type="range"
                            min="10"
                            max="100"
                            className="BrushInput"
                            id="brush-size-input"
                            value={brushSize}
                            ref={brushSizeRef}
                            onChange={(e) => setBrushSize(parseInt(e.target.value))}
                            disabled={isDisable}
                        />

                        <div className="Revert d-flex align-items-center">
                            <span className="CustomTitle">Revert all changes</span>
                            <Button
                                onClick={resetAll}
                                className="btn-default revert-all-changes-btn"
                                disabled={undoStack.length <= 1 || isDisable}
                            >
                                <Revert width={20} height={20} />
                            </Button>
                        </div>
                    </div>

                    <Button
                        className="ApplyButton"
                        onClick={handleApply}
                        disabled={undoStack.length <= 1 || isApplying || isDisable}
                    >
                        {isApplying && <Spinner size="sm" className="mr-2"></Spinner>}
                        {isApplying ? 'Applying changes...' : 'Apply'}
                    </Button>
                </div>
            </div>
        </Modal>
    )
}

export default RemoveBackgroundModal

